import { motion } from "framer-motion";
import type { MapArea } from "../../content/map";

type Props = {
  area: MapArea;
  isJa?: boolean;
  message?: string;
  size?: "sm" | "md" | "lg";
  onClick?: () => void;
  className?: string;
};

const EMOTE_ICONS: Record<string, string> = {
  note: "🎵",
  sparkle: "✨",
  heart: "💖",
  exclamation: "❗",
  question: "❓",
};

export function AnimalBubble({
  area,
  isJa = true,
  message,
  size = "md",
  onClick,
  className = "",
}: Props) {
  const emote = EMOTE_ICONS[area.residentEmote] || "✨";
  const speechText = message ?? (isJa ? area.residentGreetingJa : area.residentGreetingEn);
  const residentName = isJa ? area.residentNameJa : area.residentNameEn;
  const residentRole = isJa ? area.residentRoleJa : area.residentRoleEn;

  const isSmall = size === "sm";
  const isLarge = size === "lg";

  return (
    <div
      onClick={onClick}
      className={`relative flex items-start gap-3 select-none ${onClick ? "cursor-pointer" : ""} ${className}`}
    >
      {/* 動物アバター + エモート */}
      <div className="relative shrink-0">
        {/* 頭上のおしゃれエモート（ふわふわ浮遊） */}
        <motion.div
          initial={{ y: 0, opacity: 0.8 }}
          animate={{
            y: [-3, 3, -3],
            rotate: [-6, 6, -6],
            scale: [0.95, 1.1, 0.95],
          }}
          transition={{
            repeat: Infinity,
            duration: 2.4,
            ease: "easeInOut",
          }}
          className="absolute -top-3 -right-1 text-sm sm:text-base z-10 pointer-events-none drop-shadow-sm"
        >
          {emote}
        </motion.div>

        {/* どうぶつ顔アイコン（話すようにピョコピョコ跳ねる） */}
        <motion.div
          animate={{
            y: [0, -4, 0],
            scale: [1, 1.04, 1],
          }}
          transition={{
            repeat: Infinity,
            duration: 3.2,
            ease: "easeInOut",
          }}
          whileHover={{ scale: 1.12, rotate: [-2, 2, 0] }}
          className={`rounded-full flex items-center justify-center shadow-md border-2 border-[#d9b45f]/40 relative overflow-hidden ${
            isSmall
              ? "w-10 h-10 text-xl"
              : isLarge
              ? "w-16 h-16 text-3xl"
              : "w-12 h-12 text-2xl"
          }`}
          style={{
            backgroundColor: area.placeholderColor,
            background: `radial-gradient(circle at 35% 35%, #ffffff 0%, ${area.placeholderColor} 85%)`,
          }}
        >
          <span className="drop-shadow-sm select-none">{area.residentEmoji}</span>
          {/* 光沢ハイライト */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-black/10 pointer-events-none" />
        </motion.div>
      </div>

      {/* 吹き出し本体（Cozyどうぶつ風） */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96, x: -4 }}
        animate={{ opacity: 1, scale: 1, x: 0 }}
        transition={{ duration: 0.25 }}
        className="relative flex-1 bg-[#fffdf7]/95 backdrop-blur-md rounded-2xl p-3 sm:p-3.5 shadow-md border border-[#d9b45f]/30 flex flex-col gap-1"
        style={{
          boxShadow: "0 4px 14px rgba(60, 20, 0, 0.08)",
        }}
      >
        {/* 吹き出しのしっぽ（左向き） */}
        <div className="absolute -left-2 top-3.5 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[8px] border-r-[#fffdf7]/95" />

        {/* 住民名前＆役割バッジ */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs sm:text-sm font-black text-[#8f332c] tracking-wide">
            {residentName}
          </span>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f4e8cc] text-[#785324] font-bold">
            {residentRole}
          </span>
        </div>

        {/* セリフ */}
        <div className="text-xs sm:text-sm text-[#273f7a] font-bold leading-relaxed">
          「{speechText}」
        </div>
      </motion.div>
    </div>
  );
}
